/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, Input, OnInit, inject} from '@angular/core';

import template from './folio-list-page.component.html';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {
  SignalAdminListColumn,
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {FolioApiService, FolioModel} from './folio-api.service';
import {AdvertisingSharedModule} from '../advertising/advertising-shared.module';

@Component({
  standalone: true,
  imports: [AdvertisingSharedModule],
  selector: 'adn-folio-list-page',
  template
})
export class FolioListPageComponent implements OnInit {
  private readonly listLifetime = inject(DestroyRef);
  readonly itemStateName = 'app.adv.folios.folio';
  readonly advertiserItemStateName = 'app.adv.advertisers.advertiser';
  readonly teamItemStateName = 'app.adm.teams.team';
  readonly lineItemStateName = 'app.adv.line-items.line-item';
  readonly tierItemStateName = 'app.adm.tiers.tier';
  readonly childColumns = [
    {param: 'name'}, {param: 'userState'}, {param: 'executionState'},
    {param: 'tier', label: 'tier'}, {param: 'objectives', numeric: true},
    {param: 'startDate'}, {param: 'endDate'}, {param: 'updateTime'}
  ];
  readonly columns: SignalAdminListColumn[];
  readonly showTeam: boolean;

  private readonly routeList = inject(SignalAdminResourceListStateFactory).create<FolioModel>(
    inject(FolioApiService), {listId: 'fullFolioList', baseTable: 'folio'}, inject(DestroyRef)
  );
  private activeList = this.routeList;
  private embeddedList = false;
  private readonly permissions: any;
  private readonly childHost: any = {};

  @Input()
  set list(value: SignalAdminResourceListState<FolioModel>) {
    if (value) {
      this.activeList = value;
      this.embeddedList = true;
    }
  }

  get list(): SignalAdminResourceListState<FolioModel> {
    return this.activeList;
  }

  @Input()
  set readOnly(value: boolean) {
    this.routeList.readOnly.set(value);
  }

  constructor(
    @Inject(LocalUserPermissions) localUserPermissions: any,
    private readonly listBinding: SignalResourceListBindingService,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService
  ) {
    this.permissions = localUserPermissions.getAllVisPermissions();
    this.showTeam = this.permissions.MANAGE_ADVERTISING === true;
    this.columns = [
      {param: 'name'},
      {param: 'externalReference', label: 'extReference'},
      {param: 'Advertiser', label: 'advertiser'},
      ...(this.showTeam ? [{param: 'Team', label: 'team'}] : []),
      {param: 'updateTime'}
    ];
    getListChildren(this.childHost, this.listBinding, OBJECT_TYPE.Folio, this.LineItem, this.listLifetime);
  }

  ngOnInit(): void {
    if (!this.embeddedList) {
      this.routeList.load();
    }
  }

  toggleChildren(item: FolioModel): void {
    this.childHost.getChildren(item);
  }

  childList(itemId: string): any {
    return this.childHost.children[itemId];
  }

  businessLinkDisabled(): boolean {
    return this.permissions.MANAGE_BUSINESS !== true;
  }

  systemLinkDisabled(): boolean {
    return this.permissions.MANAGE_SYSTEM !== true;
  }
}
